import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { pageVariants } from './utils';
import { useGuestPortal } from './GuestPortalContext';

export function HelpView({ setView }) {
  const { t } = useGuestPortal();
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-2xl"
    >
      <Card className="glass-card p-6 md:p-8" data-testid="help-view-card">
        <CardHeader className="pb-4">
          <button 
            onClick={() => setView("menu")} 
            className="text-vault-text-secondary hover:text-vault-gold transition-colors mb-4 flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Menu
          </button>
          <CardTitle className="font-outfit text-2xl font-bold text-vault-text tracking-tight flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-vault-gold" />
            How to Use Guest Portal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Video Tutorial */}
          <div className="bg-vault-surface-highlight/50 border-2 border-vault-gold rounded-lg p-4">
            <h3 className="font-outfit text-lg font-bold text-vault-gold mb-3 flex items-center gap-2">
              🎥 Video Tutorial
            </h3>
            <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.loom.com/embed/b0647a5a59e34f42826cd2a16a5ab233"
                frameBorder="0"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                data-testid="help-video"
                title="Help Video"
              />
            </div>
            <p className="text-vault-text-secondary text-xs mt-2 text-center">
              Watch how to Check-In and Check-Out
            </p>
          </div>

          {/* Step 1: Check In */}
          <div className="bg-vault-surface-highlight/50 border border-vault-border rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <span className="text-black font-bold text-lg">1</span>
              </div>
              <div>
                <h3 className="font-outfit text-lg font-bold text-emerald-400 mb-1">CHECK IN</h3>
                <ul className="text-vault-text-secondary text-sm space-y-1">
                  <li>• Tap the <span className="text-emerald-400 font-medium">GREEN "Check In"</span> button</li>
                  <li>• Enter your <span className="text-vault-text">Employee Number</span> — your name appears automatically</li>
                  <li>• <span className="text-amber-400">First time?</span> Tap the orange "Register" button</li>
                  <li>• Enter your assigned <span className="text-vault-text">Room Number</span></li>
                  <li>• Date and time are auto-filled</li>
                  <li>• Sign your name in the <span className="text-vault-text">Signature Box</span></li>
                  <li>• Tap "Complete Check-In"</li>
                </ul>
                <p className="text-vault-gold text-xs mt-2 italic">Everything is on one screen - no extra steps!</p>
              </div>
            </div>
          </div>

          {/* Step 2: Check Out */}
          <div className="bg-vault-surface-highlight/50 border border-vault-border rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <div>
                <h3 className="font-outfit text-lg font-bold text-red-400 mb-1">CHECK OUT</h3>
                <ul className="text-vault-text-secondary text-sm space-y-1">
                  <li>• Tap the <span className="text-red-400 font-medium">RED "Check Out"</span> button</li>
                  <li>• Enter your <span className="text-vault-text">Room Number</span></li>
                  <li>• Select <span className="text-vault-text">Check-Out Date</span></li>
                  <li>• Enter <span className="text-vault-text">Check-Out Time</span> (24hr format)</li>
                  <li>• Tap "Complete Check-Out"</li>
                </ul>
                <p className="text-vault-gold text-xs mt-2 italic">No signature needed - your check-in signature is used automatically.</p>
              </div>
            </div>
          </div>

          {/* Not Found? */}
          <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">?</span>
              </div>
              <div>
                <h3 className="font-outfit text-lg font-bold text-red-400 mb-1">EMPLOYEE ID NOT FOUND?</h3>
                <ul className="text-vault-text-secondary text-sm space-y-1">
                  <li>• If your ID is not in the system, tap <span className="text-red-400">"Request Access"</span></li>
                  <li>• Admin will be notified via Telegram</li>
                  <li>• Please wait for admin to add you to the system</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Time Reference */}
          <div className="bg-black/50 border border-vault-gold/30 rounded-lg p-4">
            <h3 className="font-outfit text-sm font-bold text-vault-gold mb-3 text-center">24-HOUR TIME REFERENCE</h3>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="text-vault-text-secondary">6 AM = <span className="text-vault-text">06:00</span></div>
              <div className="text-vault-text-secondary">12 PM = <span className="text-vault-text">12:00</span></div>
              <div className="text-vault-text-secondary">6 PM = <span className="text-vault-text">18:00</span></div>
              <div className="text-vault-text-secondary">8 AM = <span className="text-vault-text">08:00</span></div>
              <div className="text-vault-text-secondary">2 PM = <span className="text-vault-text">14:00</span></div>
              <div className="text-vault-text-secondary">10 PM = <span className="text-vault-text">22:00</span></div>
            </div>
          </div>

          {/* Help Footer */}
          <div className="text-center pt-2">
            <p className="text-vault-text-secondary text-sm">
              Need assistance? Please contact front desk staff.
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
